import React, { useState } from "react";
import { Download, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { MiniChart } from "./MiniChart";
import { downloadCard, shareCard, canShareFiles } from "@/lib/cardExport";

const CALL_CLS = { LONG: "text-tc-bull", SHORT: "text-tc-bear", "NO TRADE": "text-tc-coach" };

export const CallCard = ({ card, readOnly = false }) => {
  const [busy, setBusy] = useState(false);
  const run = async (fn, label) => {
    setBusy(true);
    try { await fn(card); }
    catch (e) { if (e?.name !== "AbortError") toast.error(`Couldn't ${label} the image.`); }
    finally { setBusy(false); }
  };
  const id = card.id.replace(/[^a-zA-Z0-9]/g, "-");
  return (
    <article className="tc-card p-4 sm:p-5 space-y-3 min-w-0" data-testid={`call-card-${id}`}>
      <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono uppercase tracking-widest">
        <span className="px-2 py-0.5 rounded bg-tc-xp/10 text-tc-xp border border-tc-xp/30" data-testid="call-card-kind">
          {card.pack ? card.pack.name : card.kind === "boss" ? "Boss challenge" : "Historical scenario"}
        </span>
        <span className="text-tc-muted">{new Date(card.earned_at).toLocaleDateString()}</span>
      </div>
      <div>
        <h3 className="font-heading text-lg font-bold leading-snug break-words" data-testid="call-card-title">{card.title}</h3>
        <div className="text-xs text-tc-muted mt-0.5 break-words" data-testid="call-card-meta">{card.instrument} · {card.date_window}</div>
      </div>
      <div className="rounded-lg bg-[#111827] p-2"><MiniChart chart={card.chart} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><div className="text-[10px] font-mono uppercase tracking-widest text-tc-muted">My call</div>
          <div className={`font-heading text-xl font-bold ${CALL_CLS[card.call] || ""}`} data-testid="call-card-call">{card.call}</div></div>
        <div><div className="text-[10px] font-mono uppercase tracking-widest text-tc-muted">Graded outcome</div>
          <div className="text-sm font-semibold" data-testid="call-card-grade">{card.grade}</div></div>
      </div>
      <blockquote className="text-sm italic text-tc-sub border-l-2 border-tc-coach/50 pl-3 break-words" data-testid="call-card-rationale">“{card.rationale}”</blockquote>
      <p className="text-xs text-tc-sub" data-testid="call-card-verdict">{card.verdict}</p>
      <div className="flex flex-wrap gap-2 text-[11px] font-mono" data-testid="call-card-gains">
        <span className="text-tc-xp">+{card.xp} XP</span>
        {card.skill_gains.map((g) => <span key={g.key} className="text-tc-mastery">{g.label} +{g.delta}</span>)}
      </div>
      {!readOnly && <div className="flex flex-wrap gap-2 pt-1">
        <button data-testid="call-card-download" disabled={busy} onClick={() => run(downloadCard, "save")}
          className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs hover:border-white/40 transition-colors disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Save image
        </button>
        {canShareFiles() && (
          <button data-testid="call-card-share" disabled={busy} onClick={() => run(shareCard, "share")}
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs hover:border-white/40 transition-colors disabled:opacity-50">
            <Share2 className="w-3.5 h-3.5" /> Share…
          </button>
        )}
      </div>}
    </article>
  );
};
